"use client";

/**
 * Customers Slider (Figma home node 6387:57385).
 *
 * Full-bleed, two-row marquee of customer proof cards that scroll in opposite
 * directions. Non-video cards reveal spotlight content on hover (desktop) or
 * tap (touch) while the marquee keeps scrolling.
 */

import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "iconoir-react";
import Image from "next/image";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { CUSTOMER_LOGO_CATALOG } from "@/components/blocks/customers-grid-catalog";
import { MarqueeTrack } from "@/components/blocks/marquee-track";
import { MuxPlayer } from "@/components/post-rich-text/mux-player";
import { Dialog, DialogClose, DialogTitle } from "@/components/ui/dialog";
import { Link } from "@/components/ui/link";
import { PlayButton } from "@/components/ui/play-button";
import {
  VideoDialogPopup,
  videoDialogCloseClass,
} from "@/components/ui/video-dialog";
import { usePrefersHover } from "@/hooks/use-prefers-hover";
import { TOTAL_BUSINESSES_FUNDED } from "@/lib/company-stats";
import { localizeCurrency } from "@/lib/localize-currency";
import { cn } from "@/lib/utils";

import {
  CUSTOMERS_INDEX_HREF,
  getCustomerQuotePreview,
  getCustomerSliderRows,
  getCustomerSliderUiCopy,
  shouldPauseCustomerMarquee,
} from "./customers-slider-data";
import type {
  CardData,
  Company,
  FounderCardData,
  ResultsCardData,
  TestimonialCardData,
  VideoCardData,
} from "./customers-slider-data";

const SPOTLIGHT_EASE = [0.23, 1, 0.32, 1] as const;

/** How the slider breaks out of its parent padding/column. */
export type CustomersSliderBleed = "none" | "rail" | "page" | "viewport";

export function CustomersSlider({
  dataTestId = "customers-slider-section",
  locale = "en",
  /**
   * - `page` (default): cancel page `md:px-10` / `lg:px-20` gutters.
   * - `rail`: fill the HomeRail bordered column including border lines.
   * - `viewport`: marquee rows span the full viewport width (home).
   * - `none`: stay within the parent column.
   */
  bleed = "page",
}: {
  dataTestId?: string;
  locale?: string;
  bleed?: CustomersSliderBleed;
} = {}) {
  const uiCopy = getCustomerSliderUiCopy(locale, TOTAL_BUSINESSES_FUNDED);
  const rows = getCustomerSliderRows(locale);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isMarqueePaused, setIsMarqueePaused] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const prefersHover = usePrefersHover();
  const pauseMarquee = shouldPauseCustomerMarquee(isMarqueePaused, expandedId);

  const expandCard = useCallback((cardId: string) => {
    setExpandedId(cardId);
  }, []);

  const toggleExpanded = useCallback((cardId: string) => {
    setExpandedId((current) => (current === cardId ? null : cardId));
  }, []);

  const closeExpanded = useCallback(() => {
    setExpandedId(null);
  }, []);

  useEffect(() => {
    if (!expandedId) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeExpanded();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeExpanded, expandedId]);

  // 100vw includes the classic scrollbar gutter, so a full-bleed breakout can
  // extend ~8–15px past the layout viewport and trigger a horizontal scrollbar.
  // Sync width to documentElement.clientWidth instead (home bleed="viewport").
  // useLayoutEffect + no 100vw fallback — off-screen sections still widen the
  // page scroll width until this runs.
  useLayoutEffect(() => {
    if (bleed !== "viewport") return;

    const el = sectionRef.current;
    if (!el) return;

    const syncViewportWidth = () => {
      el.style.setProperty(
        "--customers-slider-viewport-width",
        `${document.documentElement.clientWidth}px`
      );
    };

    syncViewportWidth();
    window.addEventListener("resize", syncViewportWidth);
    return () => window.removeEventListener("resize", syncViewportWidth);
  }, [bleed]);

  useEffect(() => {
    if (!expandedId) return;

    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (sectionRef.current?.contains(target)) return;
      closeExpanded();
    }

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [closeExpanded, expandedId]);

  return (
    <div
      ref={sectionRef}
      data-testid={dataTestId}
      className={cn(
        "bg-grey-50 flex flex-col gap-8 py-8 md:gap-12 md:py-20",
        bleed === "page" && "md:-mx-10 lg:-mx-20",
        bleed === "rail" && "lg:-mx-px lg:w-[calc(100%+2px)]",
        bleed === "viewport" &&
          "ml-[calc(50%-50vw)] w-[var(--customers-slider-viewport-width)] max-w-[var(--customers-slider-viewport-width)]",
        bleed !== "viewport" && "items-center"
      )}
    >
      <h2 className="text-grey-950 mx-auto w-full px-6 text-left text-[24px] leading-[1.2] tracking-[-0.96px] md:w-auto md:max-w-[492px] md:text-center md:text-[32px] md:tracking-[-0.04em] md:text-balance lg:text-[40px]">
        <span className="md:hidden">{uiCopy.headingMobile}</span>
        <span className="hidden md:inline">{uiCopy.headingDesktop}</span>
      </h2>

      <div className="relative flex w-full flex-col gap-2 overflow-hidden md:gap-5">
        <MarqueeRow
          rowId="top"
          cards={localizeCurrency(rows.top, locale)}
          ctaLabel={uiCopy.ctaLabel}
          ctaIndexLabel={uiCopy.ctaIndexLabel}
          expandedId={expandedId}
          isMarqueePaused={pauseMarquee}
          prefersHover={prefersHover}
          direction="rtl"
          onExpand={expandCard}
          onCollapse={closeExpanded}
          onToggle={toggleExpanded}
          onVideoDialogOpenChange={setIsMarqueePaused}
        />
        <MarqueeRow
          rowId="bottom"
          cards={localizeCurrency(rows.bottom, locale)}
          ctaLabel={uiCopy.ctaLabel}
          ctaIndexLabel={uiCopy.ctaIndexLabel}
          expandedId={expandedId}
          isMarqueePaused={pauseMarquee}
          prefersHover={prefersHover}
          direction="ltr"
          onExpand={expandCard}
          onCollapse={closeExpanded}
          onToggle={toggleExpanded}
          onVideoDialogOpenChange={setIsMarqueePaused}
        />
      </div>
    </div>
  );
}

function MarqueeRow({
  rowId,
  cards,
  ctaLabel,
  ctaIndexLabel,
  expandedId,
  isMarqueePaused,
  prefersHover,
  direction,
  onExpand,
  onCollapse,
  onToggle,
  onVideoDialogOpenChange,
}: {
  rowId: string;
  cards: CardData[];
  ctaLabel: string;
  ctaIndexLabel: string;
  expandedId: string | null;
  isMarqueePaused: boolean;
  prefersHover: boolean;
  direction: "ltr" | "rtl";
  onExpand: (cardId: string) => void;
  onCollapse: () => void;
  onToggle: (cardId: string) => void;
  onVideoDialogOpenChange: (open: boolean) => void;
}) {
  const track = [...cards, ...cards];

  return (
    <MarqueeTrack
      direction={direction}
      paused={isMarqueePaused}
      trackClassName="gap-2 pr-2 md:gap-5 md:pr-5"
    >
      {track.map((card, index) => {
        const sourceIndex = index % cards.length;
        const cardId = `${rowId}-${sourceIndex}`;
        const isExpanded = expandedId === cardId;

        return (
          <li
            key={`${cardId}-${index >= cards.length ? "dup" : "orig"}`}
            aria-hidden={index >= cards.length}
            className={cn("shrink-0", isExpanded && "relative z-10")}
          >
            <Card
              card={card}
              ctaLabel={ctaLabel}
              ctaIndexLabel={ctaIndexLabel}
              cardId={cardId}
              isExpanded={isExpanded}
              isDuplicate={index >= cards.length}
              prefersHover={prefersHover}
              onExpand={onExpand}
              onCollapse={onCollapse}
              onToggle={onToggle}
              onVideoDialogOpenChange={onVideoDialogOpenChange}
            />
          </li>
        );
      })}
    </MarqueeTrack>
  );
}

const CARD_BASE =
  "relative h-[173px] w-[260px] shrink-0 rounded-2xl md:h-[266px] md:w-[400px] lg:h-[280px]";
const CARD_CONTENT_CLIP = "absolute inset-0 overflow-hidden rounded-2xl";
const CARD_WHITE = "bg-grey-0";
const CARD_BORDERED_SHELL = "border-grey-100 bg-grey-0 border";

function getCardShellClass(card: Exclude<CardData, { kind: "video" }>) {
  if (card.kind === "results" && card.variant !== "white") {
    return CARD_BASE;
  }

  return cn(CARD_BASE, CARD_BORDERED_SHELL);
}

function Card({
  card,
  ctaLabel,
  ctaIndexLabel,
  cardId,
  isExpanded,
  isDuplicate,
  prefersHover,
  onExpand,
  onCollapse,
  onToggle,
  onVideoDialogOpenChange,
}: {
  card: CardData;
  ctaLabel: string;
  ctaIndexLabel: string;
  cardId: string;
  isExpanded: boolean;
  isDuplicate: boolean;
  prefersHover: boolean;
  onExpand: (cardId: string) => void;
  onCollapse: () => void;
  onToggle: (cardId: string) => void;
  onVideoDialogOpenChange: (open: boolean) => void;
}) {
  if (card.kind === "video") {
    return (
      <VideoCard card={card} onDialogOpenChange={onVideoDialogOpenChange} />
    );
  }

  return (
    <button
      type="button"
      aria-expanded={isExpanded}
      aria-controls={isExpanded ? `${cardId}-overlay` : undefined}
      aria-hidden={isDuplicate ? true : undefined}
      tabIndex={isDuplicate ? -1 : 0}
      onPointerEnter={() => {
        if (prefersHover) onExpand(cardId);
      }}
      onPointerLeave={() => {
        if (prefersHover) onCollapse();
      }}
      onClick={() => {
        if (!prefersHover) onToggle(cardId);
      }}
      className={cn(
        getCardShellClass(card),
        "text-left transition-transform duration-200 ease-out motion-reduce:transition-none",
        !prefersHover && "active:scale-[0.97] motion-reduce:active:scale-100",
        isExpanded && "z-10 border-0"
      )}
    >
      <div className={CARD_CONTENT_CLIP}>
        <CardSurface card={card} isExpanded={isExpanded} />
      </div>
      <CardHoverPlaceholder
        id={`${cardId}-overlay`}
        prefersHover={prefersHover}
        isExpanded={isExpanded}
        label={
          card.storyHref === CUSTOMERS_INDEX_HREF ? ctaIndexLabel : ctaLabel
        }
        href={card.storyHref}
        detail={getExpandedDetail(card)}
        linkTone={
          card.kind === "results" && card.variant !== "white" ? "light" : "dark"
        }
      />
    </button>
  );
}

function getExpandedDetail(card: Exclude<CardData, { kind: "video" }>) {
  switch (card.kind) {
    case "founder":
    case "testimonial":
      return card.expandedQuote;
    case "results":
      return card.expandedDetail;
  }
}

function CardSurface({
  card,
  isExpanded,
}: {
  card: Exclude<CardData, { kind: "video" }>;
  isExpanded: boolean;
}) {
  const contentClassName = cn(
    "h-full w-full transition-[transform,filter,opacity] duration-500 ease-out motion-reduce:transform-none motion-reduce:filter-none motion-reduce:transition-none",
    isExpanded &&
      "scale-110 opacity-80 blur-[5px] motion-reduce:scale-100 motion-reduce:opacity-100 motion-reduce:blur-none"
  );

  switch (card.kind) {
    case "founder":
      return (
        <div
          className={cn(
            contentClassName,
            CARD_WHITE,
            "flex min-h-0 flex-col gap-3 overflow-hidden p-4 md:gap-6 md:p-8"
          )}
        >
          <FounderCardBody {...card} />
        </div>
      );
    case "testimonial":
      return (
        <div
          className={cn(
            contentClassName,
            CARD_WHITE,
            "flex min-h-0 flex-col gap-3 overflow-hidden p-4 md:gap-6 md:p-8"
          )}
        >
          <TestimonialCardBody {...card} />
        </div>
      );
    case "results":
      return (
        <div
          className={cn(
            contentClassName,
            "flex min-h-0 flex-col justify-between overflow-hidden p-4 md:p-8",
            getResultsSurfaceClass(card)
          )}
        >
          <ResultsCardBody {...card} />
        </div>
      );
  }
}

function FounderCardBody({
  name,
  role,
  quote,
  avatarSrc,
  avatarPosition,
}: FounderCardData) {
  const preview = getCustomerQuotePreview(quote);

  return (
    <>
      <div className="flex min-h-0 items-center gap-2.5 md:gap-4">
        <div className="bg-grey-0 relative size-10 shrink-0 overflow-hidden rounded-lg md:size-16">
          <Image
            src={avatarSrc}
            alt={name}
            fill
            sizes="(min-width: 768px) 64px, 40px"
            className="object-cover"
            style={{ objectPosition: avatarPosition }}
          />
        </div>
        <div className="flex min-w-0 flex-col">
          <p className="text-grey-950 truncate text-[12px] tracking-[-0.02em] md:text-[16px]">
            {name}
          </p>
          <p className="text-grey-500 truncate text-[11px] tracking-[-0.02em] md:text-[14px]">
            {role}
          </p>
        </div>
      </div>
      <div className="bg-grey-100 h-px w-full shrink-0" />
      <p className="text-grey-950 min-h-0 flex-1 text-[12px] leading-[1.3] tracking-[-0.02em] md:text-[18px]">
        {preview.text}
      </p>
    </>
  );
}

function TestimonialCardBody({ quote, company }: TestimonialCardData) {
  const preview = getCustomerQuotePreview(quote);

  return (
    <>
      <p className="text-grey-950 min-h-0 flex-1 text-[12px] leading-[1.4] tracking-[-0.02em] md:text-[18px] md:leading-[1.3]">
        {preview.text}
      </p>
      <CompanyLogo company={company} />
    </>
  );
}

function ResultsCardBody({ variant, company, stat, label }: ResultsCardData) {
  const isWhite = variant === "white";
  const isDark = variant === "dark";

  return (
    <>
      <CompanyLogo company={company} tone={isWhite ? "grey" : "white"} />
      <div className="flex min-h-0 flex-col">
        <p
          className={cn(
            "text-[40px] leading-[1.05] tracking-[-1.6px] md:text-[64px] md:leading-[1.1] md:tracking-[-2.56px]",
            isWhite && "text-grey-950",
            variant === "blue" && "text-white",
            isDark && "bg-clip-text text-transparent"
          )}
          style={{
            ...(isDark ? { backgroundImage: STAT_GRADIENT } : undefined),
          }}
        >
          {stat}
        </p>
        <p
          className={cn(
            "line-clamp-2 text-[11px] tracking-[-0.02em] md:text-[14px]",
            isWhite ? "text-grey-950" : "text-white"
          )}
        >
          {label}
        </p>
      </div>
    </>
  );
}

function getResultsSurfaceClass({ variant }: ResultsCardData) {
  if (variant === "blue") return "bg-[#001eff]";
  if (variant === "dark") return "bg-grey-950";
  return CARD_WHITE;
}

function CardHoverPlaceholder({
  id,
  prefersHover,
  isExpanded,
  label,
  href,
  detail,
  linkTone,
}: {
  id: string;
  prefersHover: boolean;
  isExpanded: boolean;
  label: string;
  href: string;
  detail: string;
  linkTone: "light" | "dark";
}) {
  const detailId = useId();
  const showTouchIdle = !prefersHover && !isExpanded;
  const showReveal = isExpanded;
  const idleLinkClass =
    linkTone === "light" ? "text-white opacity-50" : "text-grey-950 opacity-50";

  if (showReveal) {
    return (
      <AnimatePresence>
        <motion.div
          id={id}
          key="overlay"
          role="dialog"
          aria-modal="true"
          aria-label={label}
          aria-describedby={detailId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: SPOTLIGHT_EASE }}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-[#001eff]/70 px-5 text-center md:gap-4 md:px-8"
        >
          <p
            id={detailId}
            className="md:text-body-sm relative z-10 max-w-[195px] text-[12px] leading-[1.4] tracking-[-0.02em] text-white/90 md:max-w-[300px]"
          >
            {detail}
          </p>
          <Link
            href={href}
            className="md:text-body-sm relative z-10 inline-flex items-center gap-1 text-[12px] font-medium text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)] transition-opacity hover:opacity-50"
            onClick={(event) => event.stopPropagation()}
          >
            {label}
            <ArrowRight className="size-4 shrink-0" strokeWidth={1.5} />
          </Link>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (showTouchIdle) {
    return (
      <div className="pointer-events-none absolute inset-0 z-10 hidden flex-col items-center justify-center px-5 text-center md:flex md:px-8">
        <Link
          href={href}
          className={cn(
            "md:text-body-sm pointer-events-auto inline-flex items-center gap-1 text-[12px] font-medium transition-opacity hover:opacity-50",
            idleLinkClass
          )}
          onClick={(event) => event.stopPropagation()}
        >
          {label}
          <ArrowRight className="size-4 shrink-0" strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  return null;
}

const STAT_GRADIENT =
  "linear-gradient(270deg, #fe634d 4.7%, #f74b81 22.7%, #e042b4 40.7%, #c739cf 58.6%, #af32e0 76.6%, #8a28f3 100.5%, #5f1ffe 124.5%, #001eff 148.5%)";

function VideoCard({
  card,
  onDialogOpenChange,
}: {
  card: VideoCardData;
  onDialogOpenChange: (open: boolean) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const previewRef = useRef<HTMLVideoElement>(null);

  const startPreview = useCallback(() => {
    const video = previewRef.current;
    if (!video) return;
    setIsPreviewing(true);
    void video.play().catch(() => {});
  }, []);

  const stopPreview = useCallback(() => {
    const video = previewRef.current;
    if (!video) return;
    setIsPreviewing(false);
    video.pause();
    video.currentTime = 0;
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) stopPreview();
      setDialogOpen(open);
      onDialogOpenChange(open);
    },
    [onDialogOpenChange, stopPreview]
  );

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        onPointerEnter={startPreview}
        onPointerLeave={stopPreview}
        className={cn(
          CARD_BASE,
          CARD_BORDERED_SHELL,
          "group relative overflow-hidden !p-0 transition-transform duration-200 ease-out motion-reduce:transition-none",
          "active:scale-[0.97] motion-reduce:active:scale-100"
        )}
        aria-label={`Play ${card.title} video`}
      >
        <video
          ref={previewRef}
          muted
          loop
          playsInline
          controls={false}
          preload="none"
          aria-hidden
          tabIndex={-1}
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source
            src={`https://stream.mux.com/${card.playbackId}/medium.mp4`}
            type="video/mp4"
          />
        </video>
        <Image
          src={card.posterSrc}
          alt={card.alt}
          fill
          sizes="(min-width: 768px) 400px, 260px"
          className={cn(
            "object-cover transition-opacity duration-300 ease-out",
            isPreviewing && "opacity-0"
          )}
        />
        <PlayButton className="absolute top-1/2 left-1/2 z-10 size-7 -translate-x-1/2 -translate-y-1/2 md:size-10" />
      </button>

      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <VideoDialogPopup initialFocus={false}>
          <DialogTitle className="sr-only">{card.title}</DialogTitle>
          <div className="rounded-container relative aspect-video overflow-hidden">
            {dialogOpen ? (
              <MuxPlayer
                key={card.playbackId}
                playbackId={card.playbackId}
                title={card.title}
                autoPlay="any"
                loading="page"
                preload="auto"
                renditionOrder="desc"
                minResolution="1080p"
                className="absolute inset-0"
              />
            ) : null}
          </div>
          <DialogClose className={videoDialogCloseClass} />
        </VideoDialogPopup>
      </Dialog>
    </>
  );
}

function CompanyLogo({
  company,
  tone = "grey",
}: {
  company: Company;
  tone?: "grey" | "white";
}) {
  const white = tone === "white";
  const entry = CUSTOMER_LOGO_CATALOG[company];

  if (!entry) return null;

  return (
    <img
      src={entry.logo.src}
      alt={entry.name}
      width={entry.logo.width}
      height={entry.logo.height}
      className={cn(
        "h-5 max-w-[86px] object-contain object-left md:h-8 md:max-w-[120px]",
        white
          ? entry.logo.treatment === "matte"
            ? // Screen-blend so an opaque white matte doesn't render as a
              // solid box on dark cards.
              "mix-blend-screen [filter:grayscale(1)_invert(1)_brightness(1.75)]"
            : "[filter:brightness(0)_invert(1)]"
          : entry.logo.treatment === "light"
            ? "[filter:brightness(0)_opacity(0.42)]"
            : "mix-blend-multiply [filter:grayscale(1)_opacity(0.55)]"
      )}
    />
  );
}
